interface VariantRule {
	variantName: string
	plantsMin: number
	plantsMax: number
	gardenMin: number
	gardenMax: number
}

interface ShopifyVariant {
	id: number
	title: string
	option1: string | null
	option2: string | null
	option3: string | null
	price: number
	available: boolean
	featured_image: {src: string} | null
}

interface ProductData {
	title: string
	featured_image: string
	variants?: ShopifyVariant[]
}

interface ProductCalculatorComponent {
	step: number
	category: 'plants' | 'garden' | null
	inputValue: number
	resultVariant: ShopifyVariant | null
	adding: boolean
	variants: ShopifyVariant[]
	productHandle: string
	productData: ProductData | null
	rules: VariantRule[]
	config: {
		questionPlants: string
		questionGarden: string
		unitPlants: string
		unitGarden: string
		rangeMaxPlants: number
		rangeMaxGarden: number
	}

	init(): Promise<void>
	chooseCategory(cat: 'plants' | 'garden'): void
	calculate(): void
	formatMoney(cents: number): string
	addToCart(): Promise<void>
	restart(): void

	readonly questionLabel: string
	readonly unitLabel: string
	readonly rangeMin: number
	readonly rangeMax: number
}

export default {
	name: 'productCalculator',
	component(): ProductCalculatorComponent {
		return {
			step: 1,
			category: null,
			inputValue: 1,
			resultVariant: null,
			adding: false,
			variants: [],
			productHandle: '',
			productData: null,
			rules: [],
			config: {
				questionPlants: '',
				questionGarden: '',
				unitPlants: '',
				unitGarden: '',
				rangeMaxPlants: 50,
				rangeMaxGarden: 120,
			},

			async init() {
				// Read config from data attributes set by Liquid
				const el = (this as any).$el as HTMLElement
				this.productHandle = el.dataset.productHandle || ''
				this.config = {
					questionPlants: el.dataset.questionPlants || 'Combien de plantes avez-vous ?',
					questionGarden: el.dataset.questionGarden || 'Quelle est la surface de votre jardin ?',
					unitPlants: el.dataset.unitPlants || 'plantes',
					unitGarden: el.dataset.unitGarden || 'm²',
					rangeMaxPlants: 50,
					rangeMaxGarden: 120,
				}

				// Parse rules from data attribute (JSON array set by Liquid)
				try {
					this.rules = JSON.parse(el.dataset.rules || '[]')
				} catch {
					this.rules = []
				}

				// Compute range max from rules
				const plantMaxes = this.rules.map(r => r.plantsMax).filter(v => v > 0)
				const gardenMaxes = this.rules.map(r => r.gardenMax).filter(v => v > 0)
				if (plantMaxes.length) this.config.rangeMaxPlants = Math.max(...plantMaxes)
				if (gardenMaxes.length) this.config.rangeMaxGarden = Math.max(...gardenMaxes)

				// Fetch product data via Shopify AJAX API
				if (this.productHandle) {
					const handle = this.productHandle.replace('shopify://products/', '')
					try {
						const res = await fetch('/products/' + handle + '.js')
						if (res.ok) {
							const data = await res.json()
							this.productData = {
								title: data.title,
								featured_image: data.featured_image,
							}
							this.variants = data.variants
						}
					} catch (e) {
						console.error('[Calculator] Failed to fetch product:', e)
					}
				}
			},

			get questionLabel(): string {
				return this.category === 'plants'
					? this.config.questionPlants
					: this.config.questionGarden
			},

			get unitLabel(): string {
				return this.category === 'plants'
					? this.config.unitPlants
					: this.config.unitGarden
			},

			get rangeMin(): number {
				return 1
			},

			get rangeMax(): number {
				return this.category === 'plants'
					? this.config.rangeMaxPlants
					: this.config.rangeMaxGarden
			},

			chooseCategory(cat: 'plants' | 'garden') {
				this.category = cat
				this.inputValue = cat === 'plants' ? 1 : 10
				this.step = 2
			},

			calculate() {
				const value = this.inputValue

				// Find matching rule based on category and value
				const matchingRule = this.rules.find(rule => {
					if (this.category === 'plants') {
						return value >= rule.plantsMin && value <= rule.plantsMax
					} else {
						return value >= rule.gardenMin && value <= rule.gardenMax
					}
				})

				if (matchingRule) {
					const target = matchingRule.variantName.toLowerCase().replace(/\s/g, '')

					this.resultVariant =
						// Exact match on title
						this.variants.find(v => v.title.toLowerCase().replace(/\s/g, '') === target) ||
						// Partial match on title
						this.variants.find(v => v.title.toLowerCase().includes(matchingRule.variantName.toLowerCase())) ||
						// Match on option values
						this.variants.find(v =>
							[v.option1, v.option2, v.option3]
								.filter(Boolean)
								.some(o => (o as string).toLowerCase().includes(matchingRule.variantName.toLowerCase()))
						) ||
						null
				} else {
					this.resultVariant = null
				}

				this.step = 3
			},

			formatMoney(cents: number): string {
				return (cents / 100).toFixed(2).replace('.', ',') + ' €'
			},

			async addToCart() {
				if (!this.resultVariant || this.adding) return
				this.adding = true

				try {
					const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer')
					const sectionsParam =
						cartEl && (cartEl as any).getSectionsToRender
							? {
									sections: (cartEl as any).getSectionsToRender().map((s: any) => s.id),
									sections_url: window.location.pathname,
								}
							: {}

					const body = JSON.stringify({
						items: [{id: this.resultVariant.id, quantity: 1}],
						...sectionsParam,
					})

					const res = await fetch((window as any).routes.cart_add_url, {
						...(window as any).fetchConfig(),
						body,
					})
					const data = await res.json()

					if (data.status) throw new Error(data.description || 'Add to cart failed')

					if (cartEl && (cartEl as any).renderContents) {
						;(cartEl as any).renderContents(data)
					}
				} catch (e) {
					console.error('Calculator ATC error:', e)
				} finally {
					this.adding = false
				}
			},

			restart() {
				this.step = 1
				this.category = null
				this.inputValue = 1
				this.resultVariant = null
			},
		}
	},
}
